import { openDB, DBSchema, IDBPDatabase } from 'idb';

export interface PrintJob {
  profileId: string;
  copies: number;
}

export interface MultiPrintConfig {
  id: string;
  enabled: boolean;
  printJobs: PrintJob[];
  delayBetweenJobs: number;
  delayBetweenCopies: number;
  created_at: string;
  updated_at: string;
}

interface MultiPrintDB extends DBSchema {
  multi_print_config: {
    key: string;
    value: MultiPrintConfig;
  };
}

const DB_NAME = 'multi-print-db';
const STORE_NAME = 'multi_print_config';
const DB_VERSION = 1;
const CONFIG_ID = 'default-config';

let dbInstance: IDBPDatabase<MultiPrintDB> | null = null;

const initDB = async (): Promise<IDBPDatabase<MultiPrintDB>> => {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<MultiPrintDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    },
  });

  const config = await dbInstance.get(STORE_NAME, CONFIG_ID);
  if (!config) {
    await initializeDefaultConfig(dbInstance);
  }

  return dbInstance;
};

const initializeDefaultConfig = async (db: IDBPDatabase<MultiPrintDB>) => {
  const defaultConfig: MultiPrintConfig = {
    id: CONFIG_ID,
    enabled: false,
    printJobs: [],
    delayBetweenJobs: 3,
    delayBetweenCopies: 3,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  await db.put(STORE_NAME, defaultConfig);
};

export const getMultiPrintConfig = async (): Promise<MultiPrintConfig> => {
  const db = await initDB();
  let config = await db.get(STORE_NAME, CONFIG_ID);
  if (!config) {
    await initializeDefaultConfig(db);
    return (await db.get(STORE_NAME, CONFIG_ID))!;
  }

  if (config.delayBetweenJobs === undefined || config.delayBetweenCopies === undefined) {
    config = {
      ...config,
      delayBetweenJobs: config.delayBetweenJobs ?? 3,
      delayBetweenCopies: config.delayBetweenCopies ?? 3,
    };
    await db.put(STORE_NAME, config);
  }

  if (config.delayBetweenJobs > 100 || config.delayBetweenCopies > 100) {
    config = {
      ...config,
      delayBetweenJobs: Math.round(config.delayBetweenJobs / 1000),
      delayBetweenCopies: Math.round(config.delayBetweenCopies / 1000),
    };
    await db.put(STORE_NAME, config);
  }

  return config;
};

export const updateMultiPrintConfig = async (
  updates: Partial<Omit<MultiPrintConfig, 'id' | 'created_at'>>
): Promise<void> => {
  const db = await initDB();
  const config = await getMultiPrintConfig();

  const updatedConfig: MultiPrintConfig = {
    ...config,
    ...updates,
    id: CONFIG_ID,
    updated_at: new Date().toISOString(),
  };

  await db.put(STORE_NAME, updatedConfig);
};

export const addPrintJob = async (printJob: PrintJob): Promise<void> => {
  const config = await getMultiPrintConfig();

  const existingJobIndex = config.printJobs.findIndex(
    job => job.profileId === printJob.profileId
  );

  if (existingJobIndex >= 0) {
    config.printJobs[existingJobIndex] = printJob;
  } else {
    config.printJobs.push(printJob);
  }

  await updateMultiPrintConfig({ printJobs: config.printJobs });
};

export const removePrintJob = async (profileId: string): Promise<void> => {
  const config = await getMultiPrintConfig();
  const printJobs = config.printJobs.filter(job => job.profileId !== profileId);
  await updateMultiPrintConfig({ printJobs });
};

export const clearAllPrintJobs = async (): Promise<void> => {
  await updateMultiPrintConfig({ printJobs: [] });
};

export const toggleMultiPrint = async (enabled: boolean): Promise<void> => {
  await updateMultiPrintConfig({ enabled });
};

export const updatePrintDelays = async (
  delayBetweenJobs: number,
  delayBetweenCopies: number
): Promise<void> => {
  await updateMultiPrintConfig({ delayBetweenJobs, delayBetweenCopies });
};
